import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Spin,
  Row,
  Col,
  Card,
  Input,
} from 'antd';
import API from 'utils/api';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import { formatReceived } from 'utils/formatValues';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import {
  baseURI,
  deleteConfiguracionDePadron,
  getConfiguracionesDePadrones,
  patchConfiguracionDePadron,
  postConfiguracionDePadron,
} from 'api/configuracion/configuracionPadrones';
import {
  getCatalogoDePadrones,
  getCatalogoDeUsosDeConfiguracionesDePadrones,
  getTiposDeAfectacion,
  getTiposDeVistasCuentaUnica,
} from 'api/catalogos';
import TransferProcesos from 'components/TransferProcesos';

// eslint-disable-next-line react/prop-types
const ConfiguracionDePadrones = ({ permission }) => {
  const [form] = Form.useForm();
  const padrones = useSelector(({ catalogs }) => catalogs.padrones);
  const [loading, setLoading] = useState(false);
  const [tipoAfectacion, setTipoAfectacion] = useState([]);
  const [tipoVista, setTipoVista] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [camposPadron, setCamposPadron] = useState([]);
  const [selectedAfectacion, setSelectedAfectacion] = useState([]);
  const [dataTransfer, setDataTransfer] = useState([]);
  const [usos, setUsos] = useState([]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const _data = await getConfiguracionesDePadrones();
      setData(formatReceived(_data));
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onCancel = () => {
    setVisible(false);
    setSelectedRowKeys([]);
    setCamposPadron([]);
    setDataTransfer([]);
    form.resetFields();
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [_afectacion, _tipoVista, _usos, _data] = await Promise.all([
        getTiposDeAfectacion(),
        getTiposDeVistasCuentaUnica(),
        getCatalogoDeUsosDeConfiguracionesDePadrones(),
        getConfiguracionesDePadrones(),
        getCatalogoDePadrones(),
      ]);
      if (mounted) {
        setTipoAfectacion(_afectacion);
        setTipoVista(_tipoVista);
        setUsos(_usos);
        setData(_data);
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };
  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue({
      ...record,
      campos_por_padron: record.configuraciones_de_campos_de_padrones
        .map((e) => e.nombre_en_la_bd),
    });
    setSelectedAfectacion(record.tipo_de_afectacion);
  };

  const onClickAdd = async () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteConfiguracionDePadron(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const padronesFiltered = padrones.filter((e) => e.id !== 5);

  const columns = [
    {
      titleText: 'Padrón',
      dataIndex: 'padron',
      key: 'padron',
      width: 300,
      catalog: {
        cat: padrones,
        labelProp: 'descripcion',
      },
    },
    {
      titleText: 'Tipo de Afectación',
      dataIndex: 'tipo_de_afectacion',
      key: 'tipo_de_afectacion',
      width: 300,
      catalog: {
        cat: tipoAfectacion,
        labelProp: 'descripcion',
      },
    },
    {
      titleText: 'Tipo de Vista Cuenta Única',
      dataIndex: 'tipo_de_vista_cuenta_unica',
      key: 'tipo_de_vista_cuenta_unica',
      width: 250,
      catalog: {
        cat: tipoVista,
        labelProp: 'descripcion',
      },
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
    },
  ];

  const campos = async () => {
    try {
      const { padron, tipo_de_afectacion } = form.getFieldsValue();
      if (padron && tipo_de_afectacion && tipo_de_afectacion !== 3) {
        setLoading(true);
        const rescampos = await API.post('/configuracion/campos-por-padron/', {
          padron,
          tipo_de_afectacion,
        });
        setCamposPadron(rescampos.data);
        const mappedCampos = rescampos.data.campos_por_padron.map((e) => ({
          id: e.nombre_en_la_bd,
          descripcion: e.label_de_descripcion,
        }));
        setDataTransfer(mappedCampos);
        setLoading(false);
      }
    } catch (error) {
      onError(error, setLoading);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      if (values.tipo_de_afectacion !== 3) {
        const arr = [];
        values.campos_por_padron.forEach((e) => {
          const match = camposPadron.campos_por_padron
            .find((_e) => e === _e.nombre_en_la_bd);
          arr.push(match);
        });
        values.campos_por_padron = arr;
      }
      const saved = await (key ? patchConfiguracionDePadron(key, values)
        : postConfiguracionDePadron(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickEdit = () => {
    setVisible(true);
    setTimeout(campos);
  };

  const rules = { required: [{ required: true }] };

  return (
    <Spin tip="Cargando..." spinning={loading}>
      {!visible && (
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          mobileColIndex={0}
          rowKey="id"
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          allowImport
          baseURI={baseURI}
          permission={permission}
        />
      )}
      {visible && (
        <Card
          className="form-container"
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Configuración`}
              onFinish={form.submit}
              onCancel={onCancel}
            />
          )}
          bordered={false}
        >
          <Form layout="vertical" form={form} scrollToFirstError onFinish={() => onFinish()}>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={8}>
                <Form.Item label="Padrón" name="padron" rules={rules.required}>
                  <Select dataSource={padronesFiltered} onChange={() => campos()} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item label="Tipo de Afectación" name="tipo_de_afectacion" rules={rules.required}>
                  <Select
                    dataSource={tipoAfectacion}
                    onSelect={setSelectedAfectacion}
                    onChange={() => campos()}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item label="Tipo de Vista Cuenta Única" name="tipo_de_vista_cuenta_unica">
                  <Select dataSource={tipoVista} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={8}>
                <Form.Item label="Uso de configuración" name="uso_de_configuracion_de_padron">
                  <Select dataSource={usos} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={16}>
                <Form.Item label="Descripción" name="descripcion">
                  <Input allowClear />
                </Form.Item>
              </Col>
              {(selectedAfectacion !== 3) && (
                <Col span={24}>
                  <TransferProcesos
                    dataSource={dataTransfer || []}
                    formItemName="campos_por_padron"
                    label="Campos por Padron"
                    labelProp="descripcion"
                    filterProp="descripcion"
                    form={form}
                  />
                </Col>
              )}
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Card>
      )}
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="La configuracion del Padron seleccionada"
        loading={loading}
      />
    </Spin>
  );
};
export default ConfiguracionDePadrones;
